import { ArrowLeft, ArrowLeftDouble, ArrowRight, ArrowRightDouble } from '@sds-system/assets';
import type { PolymorphicRef } from '@sds-system/types';
import type { ElementType } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { Box } from '../Box';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { Typography } from '../Typography';
import { StyledPaginationWrapper } from './Pagination.styled';
import type { PaginationComponent, PaginationProps } from './Pagination.types';

export const Pagination: PaginationComponent = forwardRef(
  <C extends ElementType = 'div'>(
    { current = 1, pageSize = 5, totalPage, onChange, ...props }: PaginationProps<C>,
    ref: PolymorphicRef<C>,
  ) => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [currentPage, setCurrentPage] = useState(current || 1);
    const startPage = Math.floor((currentPage - 1) / pageSize) * pageSize + 1;

    // 테스트 주석
    const handleClickPage = (page: number) => () => changePage(page);

    const changePage = (page: number) => {
      if (page < 1 || page > totalPage) return;

      setCurrentPage(page);
      onChange?.(page);
    };

    useEffect(() => {
      setCurrentPage(current);
    }, [current, pageSize]);

    return (
      <StyledPaginationWrapper
        alignItems='center'
        display='flex'
        ref={ref}
        width='max-content'
        {...props}
      >
        {!isMobile && (
          <IconButton
            disabled={currentPage <= pageSize}
            m={2}
            onClick={handleClickPage(startPage - 1)}
            size={'S'}
            variant={'ghost.assistive'}
          >
            <ArrowLeftDouble height={20} width={20} />
          </IconButton>
        )}
        <IconButton
          disabled={currentPage === 1}
          m={2}
          onClick={handleClickPage(currentPage - 1)}
          size={'S'}
          variant={'ghost.assistive'}
        >
          <ArrowLeft height={20} width={20} />
        </IconButton>
        {!isMobile ? (
          Array(
            Math.max(1, startPage + pageSize > totalPage ? totalPage - startPage + 1 : pageSize),
          )
            .fill(0)
            .map((_, i) => (
              <Button
                color={currentPage !== startPage + i ? 'text.tertiary' : 'text.interactive.primary'}
                // biome-ignore lint/suspicious/noArrayIndexKey: unique값 없음
                key={i}
                onClick={handleClickPage(startPage + i)}
                size='L'
                styledComponents={{
                  root: {
                    height: 32,
                    width: 36,
                  },
                }}
                variant={currentPage === startPage + i ? 'ghost.primary' : 'ghost.assistive'}
              >
                {String(startPage + i)}
              </Button>
            ))
        ) : (
          <Box display='flex'>
            <Typography
              color='text.interactive.primary'
              fontType='body2.medium'
              textAlign='center'
              width={36}
            >
              {currentPage}
            </Typography>
            <Typography color='text.tertiary' fontType='body2.medium' textAlign='center' width={16}>
              /
            </Typography>
            <Typography color='text.tertiary' fontType='body2.medium' textAlign='center' width={36}>
              {totalPage}
            </Typography>
          </Box>
        )}
        <IconButton
          disabled={currentPage === totalPage}
          m={2}
          onClick={handleClickPage(currentPage + 1)}
          size={'S'}
          variant={'ghost.assistive'}
        >
          <ArrowRight height={20} width={20} />
        </IconButton>
        {!isMobile && (
          <IconButton
            disabled={currentPage > totalPage - (totalPage % pageSize || pageSize)}
            m={2}
            onClick={handleClickPage(startPage + pageSize)}
            size={'S'}
            variant={'ghost.assistive'}
          >
            <ArrowRightDouble height={20} width={20} />
          </IconButton>
        )}
      </StyledPaginationWrapper>
    );
  },
);
